import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
    ArrowLeftIcon,
    BookmarkIcon,
    ChatBubbleLeftRightIcon,
    CheckIcon,
    ChevronRightIcon,
    ClipboardDocumentCheckIcon,
    ClipboardDocumentIcon,
    FlagIcon,
} from "@heroicons/react/24/outline";
import { BookmarkIcon as BookmarkSolidIcon } from "@heroicons/react/24/solid";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import ReportModal from "../components/ReportModal";
import SubscriptionStatusChip from "../components/SubscriptionStatusChip";
import QBankQuestionImage, { QBankImage } from "../components/QBankQuestionImage";
import { useAuthContext } from "../contexts/AuthContext";
import styles from "./QBankCollection.module.css";
import { authenticatedFetch } from "../utils/authenticatedFetch";
import { rememberAuthReturn } from "../utils/conversionJourney";
import {
    activateSdleSalesEntry,
    getSdleSalesContext,
    premiumPlusPath,
    withSdleSalesEntry,
} from "../utils/qbankCampaign";
import { isQBankCode, qbankDomain, qbankLabel, QBankCode } from "../utils/qbankProfile";

interface Specialty {
    slug: string;
    name: string;
    count: number;
    section?: string;
}

interface Question {
    id: string;
    question_text: string;
    options: { label: string; text: string }[];
    specialty: string;
    difficulty: string;
    revealed?: boolean;
    correct_answer?: string;
    explanation?: string;
    question_references?: any[];
    explainer_status?: string;
    question_image?: QBankImage | null;
}

interface Collection {
    id: string;
    title: string;
    year: number;
    month: number;
    questions_count: number;
}

interface SimilarQuestion extends Question {
    collection_id?: string;
    collection?: {
        id: string;
        title: string;
        year?: number;
        month?: number;
    } | null;
    similarity_score: number;
    rank?: number;
}

const QUESTION_TRANSITION_MS = 320;
const QUESTION_SCROLL_SETTLE_LIMIT_MS = 400;

const QBankCollection: React.FC = () => {
    const { bank: bankParam, collectionId } = useParams<{ bank: string; collectionId: string }>();
    const bank: QBankCode = isQBankCode(bankParam) ? bankParam : "sdle";
    const { user, loading: authLoading } = useAuthContext();
    const navigate = useNavigate();
    const location = useLocation();

    const [collection, setCollection] = useState<Collection | null>(null);
    const [specialties, setSpecialties] = useState<Specialty[]>([]);
    const [selectedSpecialty, setSelectedSpecialty] = useState<string | null>(null);
    const [questions, setQuestions] = useState<Question[]>([]);
    const [loading, setLoading] = useState(true);
    const [questionsLoading, setQuestionsLoading] = useState(false);
    const [revealingId, setRevealingId] = useState<string | null>(null);
    const [credits, setCredits] = useState<number>(0);
    const [subscriptionPlan, setSubscriptionPlan] = useState<string | null>(null);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [mountedQuestionIds, setMountedQuestionIds] = useState<Set<string>>(new Set());
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [similarByQuestionId, setSimilarByQuestionId] = useState<Record<string, SimilarQuestion[]>>({});
    const [similarLoadingIds, setSimilarLoadingIds] = useState<Set<string>>(new Set());
    const [similarErrorIds, setSimilarErrorIds] = useState<Set<string>>(new Set());
    const [expandedSimilarIds, setExpandedSimilarIds] = useState<Set<string>>(new Set());
    const [collapsedReferenceIds, setCollapsedReferenceIds] = useState<Set<string>>(new Set());
    const [isSalesEntry, setIsSalesEntry] = useState(() => bank === "sdle" && activateSdleSalesEntry(location.search));
    const [salesContext, setSalesContext] = useState(() => getSdleSalesContext(location.search));

    // Bookmark and Report state
    const [bookmarkedIds, setBookmarkedIds] = useState<Set<string>>(new Set());
    const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);
    const [reportingQuestionId, setReportingQuestionId] = useState<string | null>(null);
    const questionScrollFrameRef = useRef<number | null>(null);
    const removeQuestionScrollListenersRef = useRef<(() => void) | null>(null);
    const pendingQuestionExpansionIdRef = useRef<string | null>(null);
    const questionsRequestIdRef = useRef(0);
    const restoredQuestionIdRef = useRef<string | null>(null);

    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }, [collectionId, bank]);

    useEffect(() => () => {
        if (questionScrollFrameRef.current !== null) {
            window.cancelAnimationFrame(questionScrollFrameRef.current);
        }
        removeQuestionScrollListenersRef.current?.();
    }, []);

    useEffect(() => {
        if (authLoading) return;
        const allowsGuestPreview = bank === "sdle" && isSalesEntry;
        if (!user && !allowsGuestPreview) {
            const returnTo = `/qbank/${bank}/collections/${collectionId}`;
            rememberAuthReturn(returnTo);
            navigate("/login", { state: { from: returnTo }, replace: true });
            return;
        }
        loadInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collectionId, bank, user?.id, authLoading, isSalesEntry]);

    useEffect(() => {
        const active = bank === "sdle" && activateSdleSalesEntry(location.search);
        setIsSalesEntry(active);
        setSalesContext(getSdleSalesContext(location.search));
    }, [bank, collectionId, location.search, user]);

    useEffect(() => {
        if (authLoading) return;

        questionsRequestIdRef.current += 1;
        setQuestions([]);
        setExpandedId(null);
        setMountedQuestionIds(new Set());
        setSimilarByQuestionId({});
        setExpandedSimilarIds(new Set());

        if (user?.id) {
            loadCredits();
            loadSubscription();
            loadBookmarks();
        } else {
            setCredits(0);
            setSubscriptionPlan(null);
            setBookmarkedIds(new Set());
            setShowBookmarkedOnly(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id, authLoading]);

    useEffect(() => {
        if (!authLoading && selectedSpecialty && collectionId) {
            loadQuestions(selectedSpecialty);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSpecialty, collectionId, user?.id, authLoading]);

    const loadInitialData = async () => {
        if (!collectionId) return;
        questionsRequestIdRef.current += 1;
        setLoading(true);
        setQuestions([]);
        setSelectedSpecialty(null);
        setSimilarByQuestionId({});
        setSimilarLoadingIds(new Set());
        setSimilarErrorIds(new Set());
        setExpandedSimilarIds(new Set());

        try {
            // Load specialties for this collection
            const specRes = await fetch(`${apiUrl}/api/qbank/collections/${collectionId}/specialties?bank=${bank}`);
            if (specRes.ok) {
                const specData = await specRes.json();
                setSpecialties(specData);

                // Auto-select first specialty - the useEffect will handle loading questions
                if (specData.length > 0) {
                    setQuestionsLoading(true); // Show loading state while useEffect loads questions
                    const requestedSpecialty = new URLSearchParams(location.search).get("specialty");
                    const selected = specData.find((specialty: Specialty) => specialty.slug === requestedSpecialty);
                    setSelectedSpecialty(selected?.slug || specData[0].slug);
                }
            }

            // Get collection details from the collections endpoint
            const collRes = await fetch(`${apiUrl}/api/qbank/collections?bank=${bank}`);
            if (collRes.ok) {
                const collections = await collRes.json();
                const found = collections.find((c: Collection) => c.id === collectionId);
                if (found) {
                    setCollection(found);
                }
            }
        } catch (err) {
            console.error("Failed to load data", err);
        } finally {
            setLoading(false);
        }
    };

    const loadQuestions = async (specialtySlug: string) => {
        if (!collectionId) return;
        const requestId = questionsRequestIdRef.current + 1;
        questionsRequestIdRef.current = requestId;
        setQuestionsLoading(true);
        setExpandedId(null);
        setMountedQuestionIds(new Set());
        setExpandedSimilarIds(new Set());
        setCollapsedReferenceIds(new Set());

        try {
            const params = new URLSearchParams({ bank });
            if (user?.id) params.set("user_id", user.id);
            const questionsUrl = `${apiUrl}/api/qbank/collections/${collectionId}/specialties/${specialtySlug}/questions?${params.toString()}`;
            const res = user?.id
                ? await authenticatedFetch(questionsUrl)
                : await fetch(questionsUrl);
            if (res.ok && requestId === questionsRequestIdRef.current) {
                const data = await res.json();
                setQuestions(data);
            }
        } catch (err) {
            console.error("Failed to load questions", err);
        } finally {
            if (requestId === questionsRequestIdRef.current) {
                setQuestionsLoading(false);
            }
        }
    };

    const loadCredits = async () => {
        if (!user?.id) return;
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/credits/${user.id}`);
            if (res.ok) {
                const data = await res.json();
                setCredits(data.credits ?? 0);
            }
        } catch (err) {
            console.error("Failed to load credits", err);
        }
    };

    const loadSubscription = async () => {
        if (!user?.id) return;
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/subscriptions/${user.id}`);
            if (res.ok) {
                const data = await res.json();
                if (data?.status === "active") {
                    setSubscriptionPlan(data.plan);
                }
            }
        } catch (err) {
            console.error("Failed to load subscription", err);
        }
    };

    const loadBookmarks = async () => {
        if (!user?.id) return;
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/qbank/bookmarks/${user.id}?bank=${bank}`);
            if (res.ok) {
                const data = await res.json();
                setBookmarkedIds(new Set(data.bookmark_ids || []));
            }
        } catch (err) {
            console.error("Failed to load bookmarks", err);
        }
    };

    const handleBookmark = async (e: React.MouseEvent, questionId: string) => {
        e.stopPropagation();
        if (!user?.id) return;

        // Optimistic update
        const wasBookmarked = bookmarkedIds.has(questionId);
        const newBookmarkedIds = new Set(bookmarkedIds);
        if (wasBookmarked) {
            newBookmarkedIds.delete(questionId);
        } else {
            newBookmarkedIds.add(questionId);
        }
        setBookmarkedIds(newBookmarkedIds);

        try {
            const res = await authenticatedFetch(`${apiUrl}/api/qbank/questions/${questionId}/bookmark`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ user_id: user.id, bank }),
            });

            if (!res.ok) {
                // Revert on error
                setBookmarkedIds(bookmarkedIds);
                throw new Error("Failed to toggle bookmark");
            }
        } catch (err) {
            console.error("Failed to toggle bookmark", err);
            setBookmarkedIds(bookmarkedIds); // Revert
        }
    };

    const handleReport = async (reason: string, details: string) => {
        if (!user?.id || !reportingQuestionId) return;

        const res = await authenticatedFetch(`${apiUrl}/api/qbank/questions/${reportingQuestionId}/report`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                user_id: user.id,
                bank,
                reason,
                details: details || null,
            }),
        });

        if (!res.ok) {
            const data = await res.json();
            throw new Error(data.error || "Failed to submit report");
        }
    };

    const handleReveal = async (e: React.MouseEvent, questionId: string) => {
        e.stopPropagation();
        if (!user?.id) {
            const returnTo = getQuestionReturnPath(questionId);
            rememberAuthReturn(returnTo);
            navigate("/login", { state: { from: returnTo } });
            return;
        }

        setRevealingId(questionId);
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/qbank/questions/${questionId}/reveal`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ user_id: user.id, bank }),
            });

            if (res.ok) {
                const revealedQuestion = await res.json();
                applyRevealedQuestion(questionId, revealedQuestion);

                if (revealedQuestion.credits_spent > 0) {
                    setCredits(prev => prev - revealedQuestion.credits_spent);
                }
            } else if (res.status === 402) {
                (window as any).showNotification?.(
                    "You need credits to reveal answers. Subscribe or purchase credits.",
                    "error"
                );
                navigate("/pricing");
            }
        } catch (err) {
            console.error("Failed to reveal answer", err);
        } finally {
            setRevealingId(null);
        }
    };

    const applyRevealedQuestion = (questionId: string, revealedQuestion: Question) => {
        setQuestions(prev =>
            prev.map(q =>
                q.id === questionId
                    ? { ...q, ...revealedQuestion, revealed: true }
                    : q
            )
        );

        setSimilarByQuestionId(prev => {
            const next: Record<string, SimilarQuestion[]> = {};
            Object.entries(prev).forEach(([sourceId, items]) => {
                next[sourceId] = items.map(item =>
                    item.id === questionId
                        ? {
                            ...item,
                            ...revealedQuestion,
                            collection: item.collection,
                            collection_id: item.collection_id,
                            similarity_score: item.similarity_score,
                            rank: item.rank,
                            revealed: true,
                        }
                        : item
                );
            });
            return next;
        });
    };

    const loadSimilarQuestions = async (questionId: string, force = false) => {
        if (!user?.id || (!force && similarByQuestionId[questionId]) || similarLoadingIds.has(questionId)) {
            return;
        }

        setSimilarLoadingIds(prev => new Set(prev).add(questionId));
        setSimilarErrorIds(prev => {
            const next = new Set(prev);
            next.delete(questionId);
            return next;
        });

        try {
            const res = await authenticatedFetch(
                `${apiUrl}/api/qbank/questions/${questionId}/similar?user_id=${user.id}&bank=${bank}&limit=5`
            );
            if (!res.ok) {
                throw new Error("Failed to load similar questions");
            }
            const data = await res.json();
            setSimilarByQuestionId(prev => ({
                ...prev,
                [questionId]: data.similar_questions || [],
            }));
        } catch (err) {
            console.error("Failed to load similar questions", err);
            setSimilarErrorIds(prev => new Set(prev).add(questionId));
        } finally {
            setSimilarLoadingIds(prev => {
                const next = new Set(prev);
                next.delete(questionId);
                return next;
            });
        }
    };

    const toggleSimilarQuestion = (id: string) => {
        setExpandedSimilarIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const toggleReferences = (questionId: string) => {
        setCollapsedReferenceIds(prev => {
            const next = new Set(prev);
            if (next.has(questionId)) {
                next.delete(questionId);
            } else {
                next.add(questionId);
            }
            return next;
        });
    };

    const toggleExpand = (id: string, element?: HTMLElement) => {
        const isPendingExpansion = pendingQuestionExpansionIdRef.current === id;
        const isExpanding = expandedId !== id && !isPendingExpansion;

        if (questionScrollFrameRef.current !== null) {
            window.cancelAnimationFrame(questionScrollFrameRef.current);
            questionScrollFrameRef.current = null;
        }
        removeQuestionScrollListenersRef.current?.();
        pendingQuestionExpansionIdRef.current = null;

        let nextScrollTop: number | null = null;
        let heightCollapsingAbove = 0;
        const collapsingBodyRegions: HTMLElement[] = [];
        if (isExpanding && element) {
            const selectedRect = element.getBoundingClientRect();

            // Previously visited bodies stay mounted so their closing animation
            // can finish. Account for every body above the selection that still
            // has height, including one already mid-collapse after a rapid click.
            const questionList = element.parentElement;
            if (questionList) {
                for (const sibling of Array.from(questionList.children)) {
                    if (sibling === element) break;

                    const bodyRegion = Array.from(sibling.children).find(
                        child => child instanceof HTMLElement && child.id.startsWith("question-body-")
                    );
                    if (bodyRegion instanceof HTMLElement) {
                        const bodyHeight = bodyRegion.getBoundingClientRect().height;
                        if (bodyHeight > 0) {
                            collapsingBodyRegions.push(bodyRegion);
                            heightCollapsingAbove += bodyHeight;
                        }
                    }
                }
            }

            const scrollMarginTop = Number.parseFloat(
                window.getComputedStyle(element).scrollMarginTop
            ) || 0;
            const finalDocumentTop = window.scrollY + selectedRect.top - heightCollapsingAbove;
            nextScrollTop = Math.max(0, finalDocumentTop - scrollMarginTop);
        }

        if (!isExpanding) {
            setExpandedId(null);
            return;
        }

        const wasMounted = mountedQuestionIds.has(id);
        const prefersReducedMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)"
        ).matches;

        setMountedQuestionIds(prev => new Set(prev).add(id));
        void loadSimilarQuestions(id);
        pendingQuestionExpansionIdRef.current = id;

        const finishExpansion = (openQuestion: boolean) => {
            questionScrollFrameRef.current = null;
            if (pendingQuestionExpansionIdRef.current !== id) {
                return;
            }
            if (element && !element.isConnected) {
                pendingQuestionExpansionIdRef.current = null;
                return;
            }

            if (openQuestion) {
                setExpandedId(id);
            }
            pendingQuestionExpansionIdRef.current = null;

            if (nextScrollTop !== null) {
                if (prefersReducedMotion) {
                    window.scrollTo({ top: nextScrollTop, behavior: "auto" });
                    return;
                }

                const startingScrollTop = window.scrollY;
                const scrollDistance = nextScrollTop - startingScrollTop;
                const startingCollapseHeight = heightCollapsingAbove;
                const startedAt = window.performance.now();
                let furthestProgress = 0;

                const removeInterruptionListeners = () => {
                    window.removeEventListener("wheel", interruptScroll);
                    window.removeEventListener("touchstart", interruptScroll);
                    window.removeEventListener("pointerdown", interruptScroll);
                    window.removeEventListener("keydown", interruptScroll);
                    if (removeQuestionScrollListenersRef.current === removeInterruptionListeners) {
                        removeQuestionScrollListenersRef.current = null;
                    }
                };

                const interruptScroll = () => {
                    if (questionScrollFrameRef.current !== null) {
                        window.cancelAnimationFrame(questionScrollFrameRef.current);
                        questionScrollFrameRef.current = null;
                    }
                    removeInterruptionListeners();
                };

                window.addEventListener("wheel", interruptScroll, { passive: true });
                window.addEventListener("touchstart", interruptScroll, { passive: true });
                window.addEventListener("pointerdown", interruptScroll, { passive: true });
                window.addEventListener("keydown", interruptScroll);
                removeQuestionScrollListenersRef.current = removeInterruptionListeners;

                const animateScroll = (timestamp: number) => {
                    if (element && !element.isConnected) {
                        interruptScroll();
                        return;
                    }

                    const elapsed = timestamp - startedAt;
                    let progress: number;

                    if (startingCollapseHeight > 0) {
                        const remainingCollapseHeight = collapsingBodyRegions.reduce(
                            (total, region) => total + region.getBoundingClientRect().height,
                            0
                        );
                        const layoutProgress = 1 - (remainingCollapseHeight / startingCollapseHeight);
                        furthestProgress = Math.max(
                            furthestProgress,
                            Math.min(1, Math.max(0, layoutProgress))
                        );
                        progress = remainingCollapseHeight <= 0.5 || elapsed >= QUESTION_SCROLL_SETTLE_LIMIT_MS
                            ? 1
                            : furthestProgress;
                    } else {
                        const timeProgress = Math.min(1, elapsed / QUESTION_TRANSITION_MS);
                        progress = 1 - Math.pow(1 - timeProgress, 3);
                    }

                    window.scrollTo({
                        top: startingScrollTop + (scrollDistance * progress),
                        behavior: "auto",
                    });

                    if (progress >= 1) {
                        questionScrollFrameRef.current = null;
                        removeInterruptionListeners();
                        return;
                    }
                    questionScrollFrameRef.current = window.requestAnimationFrame(animateScroll);
                };

                questionScrollFrameRef.current = window.requestAnimationFrame(animateScroll);
            }
        };

        if (wasMounted || prefersReducedMotion) {
            setExpandedId(id);
            questionScrollFrameRef.current = window.requestAnimationFrame(() => {
                finishExpansion(false);
            });
            return;
        }

        // Give a newly mounted body one closed paint before opening it. Start
        // the previous collapse during that paint so the selected card is
        // already moving into place when the controlled scroll begins.
        setExpandedId(null);
        questionScrollFrameRef.current = window.requestAnimationFrame(() => {
            questionScrollFrameRef.current = window.requestAnimationFrame(() => {
                finishExpansion(true);
            });
        });
    };

    const getDifficultyClass = (difficulty: string) => {
        switch (difficulty) {
            case "easy": return styles.difficultyEasy;
            case "medium": return styles.difficultyMedium;
            case "hard": return styles.difficultyHard;
            default: return "";
        }
    };

    const handleDiscussWithAI = (e: React.MouseEvent, question: Question) => {
        e.stopPropagation();
        if (question.question_image) return;

        // Format options as a readable list
        const optionsText = question.options
            .map((opt) => `${opt.label}. ${opt.text}`)
            .join("\n");

        // Build the message for Agent4
        const message = `I'm studying a question from the QBank and need help understanding it:

**Question:**
${question.question_text}

**Options:**
${optionsText}

**Correct Answer:** ${question.correct_answer}

Please explain why ${question.correct_answer} is the correct answer and why the other options are incorrect. Help me understand the underlying concepts.`;

        // Store the question data in sessionStorage for the new tab to pick up
        const stateData = {
            newQuestion: message,
            specialty: question.specialty,
            inputType: "thread",
            domain: qbankDomain(bank),
        };
        sessionStorage.setItem('qbank_discuss_data', JSON.stringify(stateData));

        // Open in new tab
        window.open("/chat?from=qbank", "_blank");
    };

    const handleShare = async (e: React.MouseEvent, question: Question) => {
        e.stopPropagation();

        const optionsText = question.options
            .map((opt) => `${opt.label}) ${opt.text}`)
            .join("\n");

        let shareText = `📋 ${bank.toUpperCase()} Question\n\nQ: ${question.question_text}\n\n${optionsText}`;
        if (question.question_image) shareText += "\n\n🖼️ This question includes an image.";

        if (question.revealed && question.correct_answer) {
            shareText += `\n\n✅ Correct: ${question.correct_answer}`;
        }

        // Add references if available
        if (question.revealed && question.question_references && question.question_references.length > 0) {
            shareText += "\n\n📚 References:\n";
            question.question_references.forEach((ref: any, index: number) => {
                let source = ref.source || ref.textbook || ref.source_name || "Source";
                if (typeof source === 'string') {
                    source = source.replace(/\.pdf$/i, "");
                }
                const page = ref.page_number || ref.details?.page_number;
                shareText += `${index + 1}. ${source}${page ? ` (p. ${page})` : ""}\n`;
            });
        }

        shareText += "\n—\nPowered by C-Shaped | c-shaped.com";

        try {
            await navigator.clipboard.writeText(shareText);
            setCopiedId(question.id);
            setTimeout(() => setCopiedId(null), 2000);
        } catch (err) {
            console.error("Failed to copy:", err);
        }
    };

    const revealedCount = questions.filter(q => q.revealed).length;
    const progressPercent = questions.length > 0
        ? Math.round((revealedCount / questions.length) * 100)
        : 0;

    const selectedSpecialtyName = specialties.find(s => s.slug === selectedSpecialty)?.name || "";
    const visibleQuestions = questions.filter(q => !showBookmarkedOnly || bookmarkedIds.has(q.id));
    const specialtySections = specialties.reduce<Record<string, Specialty[]>>((groups, specialty) => {
        const section = specialty.section || "Specialties";
        groups[section] = [...(groups[section] || []), specialty];
        return groups;
    }, {});

    const getQuestionReturnPath = (questionId: string) => {
        const params = new URLSearchParams(location.search);
        params.set("question", questionId);
        if (selectedSpecialty) params.set("specialty", selectedSpecialty);
        const path = `/qbank/${bank}/collections/${collectionId}?${params.toString()}`;
        return isSalesEntry ? withSdleSalesEntry(path, salesContext) : path;
    };

    const handleCampaignUpgrade = (questionId: string) => {
        const returnTo = getQuestionReturnPath(questionId);
        navigate(premiumPlusPath(returnTo));
    };

    const handleGuestSignIn = (questionId: string) => {
        const returnTo = getQuestionReturnPath(questionId);
        rememberAuthReturn(returnTo);
        navigate("/login", { state: { from: returnTo } });
    };

    useEffect(() => {
        const questionId = new URLSearchParams(location.search).get("question");
        if (
            !questionId
            || questionsLoading
            || restoredQuestionIdRef.current === questionId
            || !questions.some(question => question.id === questionId)
        ) return;

        const element = document.getElementById(`question-${questionId}`);
        if (!element) return;
        restoredQuestionIdRef.current = questionId;
        toggleExpand(questionId, element);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, questions, questionsLoading]);

    const renderSimilarQuestions = (questionId: string) => {
        const isLoading = similarLoadingIds.has(questionId);
        const hasError = similarErrorIds.has(questionId);
        const similarQuestions = similarByQuestionId[questionId] || [];

        return (
            <section className={styles.similarSection}>
                <div className={styles.similarHeader}>
                    <div>
                        <h4>Similar questions</h4>
                        <p>From other collections in this specialty</p>
                    </div>
                    {hasError && (
                        <button
                            type="button"
                            className={styles.similarRetry}
                            onClick={(e) => {
                                e.stopPropagation();
                                setSimilarByQuestionId(prev => {
                                    const next = { ...prev };
                                    delete next[questionId];
                                    return next;
                                });
                                void loadSimilarQuestions(questionId, true);
                            }}
                        >
                            Retry
                        </button>
                    )}
                </div>

                {isLoading ? (
                    <div className={styles.similarStatus}>
                        <span className={styles.inlineSpinner} aria-hidden="true" />
                        <span>Finding similar questions...</span>
                    </div>
                ) : hasError ? (
                    <p className={styles.similarEmpty}>Could not load similar questions.</p>
                ) : similarQuestions.length === 0 ? (
                    <p className={styles.similarEmpty}>No similar questions found yet.</p>
                ) : (
                    <div className={styles.similarList}>
                        {similarQuestions.map((similar) => {
                            const similarCorrectOption = similar.options.find(option => option.label === similar.correct_answer);
                            const isSimilarRevealing = revealingId === similar.id;
                            const similarExpansionKey = `${questionId}:${similar.id}`;
                            const isSimilarExpanded = expandedSimilarIds.has(similarExpansionKey);
                            const similarDetailsId = `similar-details-${questionId}-${similar.id}`;

                            return (
                                <article
                                    key={similar.id}
                                    className={`${styles.similarItem} ${isSimilarExpanded ? styles.expandedSimilar : ""}`}
                                >
                                    <button
                                        type="button"
                                        className={styles.similarSummary}
                                        onClick={() => toggleSimilarQuestion(similarExpansionKey)}
                                        aria-expanded={isSimilarExpanded}
                                        aria-controls={similarDetailsId}
                                    >
                                        <div className={styles.similarSummaryContent}>
                                            <div className={styles.similarMeta}>
                                                <span>{similar.collection?.title || "Other collection"}</span>
                                                <span>{similar.specialty}</span>
                                                {similar.difficulty && (
                                                    <span className={`${styles.difficultyTag} ${getDifficultyClass(similar.difficulty)}`}>
                                                        {similar.difficulty}
                                                    </span>
                                                )}
                                                {similar.revealed && (
                                                    <span className={styles.revealedTag}>
                                                        <CheckIcon aria-hidden="true" />
                                                        Revealed
                                                    </span>
                                                )}
                                            </div>

                                            <p className={styles.similarQuestionText}>{similar.question_text}</p>
                                        </div>

                                        <ChevronRightIcon
                                            className={styles.similarExpandIcon}
                                            aria-hidden="true"
                                        />
                                    </button>

                                    <div
                                        id={similarDetailsId}
                                        className={`${styles.similarDetailsRegion} ${isSimilarExpanded ? styles.similarDetailsRegionOpen : ""}`}
                                        aria-hidden={!isSimilarExpanded}
                                    >
                                        <div className={styles.similarDetailsClip}>
                                            <div className={styles.similarDetails}>
                                                <QBankQuestionImage image={similar.question_image} compact />
                                                <div className={styles.similarOptions}>
                                                {similar.options.map((option) => {
                                                    const isCorrect = similar.revealed && option.label === similar.correct_answer;
                                                    return (
                                                        <div
                                                            key={option.label || option.text}
                                                            className={`${styles.similarOption} ${isCorrect ? styles.correctOption : ""}`}
                                                        >
                                                            <span className={styles.optionLabel}>{option.label}</span>
                                                            <span className={styles.optionText}>{option.text}</span>
                                                        </div>
                                                    );
                                                })}
                                                </div>

                                                {similar.revealed ? (
                                                    <div className={styles.similarAnswer}>
                                                        <strong>
                                                            Option {similar.correct_answer}
                                                            {similarCorrectOption ? `: ${similarCorrectOption.text}` : ""}
                                                        </strong>
                                                        {similar.explanation && (
                                                            <div className={styles.similarExplanation}>
                                                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                                    {similar.explanation}
                                                                </ReactMarkdown>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className={styles.similarLocked}>
                                                        <span>Answer locked</span>
                                                        <button
                                                            type="button"
                                                            className={styles.similarRevealButton}
                                                            onClick={(e) => handleReveal(e, similar.id)}
                                                            disabled={isSimilarRevealing}
                                                        >
                                                            {isSimilarRevealing ? "Revealing..." : "Reveal"}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )}
            </section>
        );
    };

    const campaignLanguage = isSalesEntry && !user ? salesContext.language : undefined;
    const isArabicCampaign = campaignLanguage === "ar";

    return (
        <div className={styles.pageContainer}>
            <SEO
                title={`${collection?.title || "Exam"} | ${qbankLabel(bank)} | C-Shaped`}
                canonical={`https://c-shaped.com/qbank/${bank}/collections/${collectionId}`}
                description={`Practice ${collection?.title || "exam"} MCQs with AI-powered explanations.`}
            />
            <Header language={campaignLanguage} />

            <main className={styles.mainContent}>
                {isSalesEntry && !user && (
                    <section
                        className={styles.campaignValueBar}
                        aria-label={isArabicCampaign ? "مزايا Premium Plus" : "Premium Plus benefits"}
                        dir={isArabicCampaign ? "rtl" : "ltr"}
                        lang={campaignLanguage}
                    >
                        <div>
                            <strong>{isArabicCampaign
                                ? "أنت تستعرض بنك أسئلة SDLE الفعلي."
                                : "You’re previewing the real SDLE Question Bank."}</strong>
                            <span>{isArabicCampaign
                                ? "تشمل Premium Plus جميع الإجابات الموثقة، وكل المجموعات، والاختبارات التجريبية."
                                : "Premium Plus includes every referenced answer, all collections, and mock exams."}</span>
                        </div>
                        <button
                            type="button"
                            onClick={() => navigate(premiumPlusPath(withSdleSalesEntry(`/qbank/sdle/collections/${collectionId}`, salesContext)))}
                        >
                            {isArabicCampaign ? "شاهد Premium Plus" : "See Premium Plus"}
                        </button>
                    </section>
                )}
                <section className={styles.studyHeader}>
                    <div className={styles.headerPrimaryRow}>
                        <Link
                            to={isSalesEntry && !user ? withSdleSalesEntry("/qbank/sdle", salesContext) : `/qbank/${bank}`}
                            className={styles.backLink}
                        >
                            <ArrowLeftIcon aria-hidden="true" />
                            <span>{isArabicCampaign ? "بنك الأسئلة" : "QBank"}</span>
                        </Link>

                        <div className={styles.accessCluster}>
                            {!user ? (
                                <Link
                                    to="/login"
                                    state={{ from: isSalesEntry ? withSdleSalesEntry(`/qbank/sdle/collections/${collectionId}`, salesContext) : `/qbank/${bank}/collections/${collectionId}` }}
                                    onClick={() => rememberAuthReturn(
                                        isSalesEntry ? withSdleSalesEntry(`/qbank/sdle/collections/${collectionId}`, salesContext) : `/qbank/${bank}/collections/${collectionId}`
                                    )}
                                    className={styles.creditsChip}
                                >
                                    {isArabicCampaign ? "تسجيل الدخول" : "Sign in"}
                                </Link>
                            ) : subscriptionPlan ? (
                                <SubscriptionStatusChip
                                    plan={subscriptionPlan}
                                    size="compact"
                                    className={styles.planChip}
                                />
                            ) : (
                                <span className={styles.creditsChip}>{credits} credits</span>
                            )}
                        </div>
                    </div>

                    <div className={styles.titleBlock}>
                        <p className={styles.eyebrow}>Study collection</p>
                        <h1>{collection?.title || "Loading collection"}</h1>
                    </div>

                    <div className={styles.progressPanel}>
                        <div className={styles.progressCopy}>
                            <strong>{user ? `${progressPercent}% revealed` : "Browse questions"}</strong>
                            <span>
                                {user
                                    ? `${revealedCount} of ${questions.length} questions in ${selectedSpecialtyName || "this specialty"}`
                                    : "Sign in to reveal answers and track your progress"}
                            </span>
                        </div>
                        {user && (
                            <div className={styles.progressTrack} aria-hidden="true">
                                <div
                                    className={styles.progressFill}
                                    style={{ width: `${progressPercent}%` }}
                                />
                            </div>
                        )}
                    </div>

                    {!loading && specialties.length > 0 && (
                        <div className={styles.studyControls}>
                            <div className={styles.specialtyTabs} aria-label="Specialties">
                                {Object.entries(specialtySections).map(([section, sectionSpecialties]) => (
                                    <div key={section} className={styles.specialtyGroup}>
                                        {bank === "smle" && <strong className={styles.specialtyGroupTitle}>{section}</strong>}
                                        <div className={styles.specialtyGroupItems}>
                                            {sectionSpecialties.map((specialty) => (
                                                <button
                                                    key={specialty.slug}
                                                    type="button"
                                                    className={`${styles.specialtyTab} ${selectedSpecialty === specialty.slug ? styles.activeTab : ""}`}
                                                    onClick={() => setSelectedSpecialty(specialty.slug)}
                                                >
                                                    <span>{specialty.name}</span>
                                                    <small>{specialty.count}</small>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {user && (
                                <button
                                    type="button"
                                    className={`${styles.bookmarkFilter} ${showBookmarkedOnly ? styles.active : ""}`}
                                    onClick={() => setShowBookmarkedOnly(!showBookmarkedOnly)}
                                    disabled={questions.length === 0}
                                >
                                    {showBookmarkedOnly ? (
                                        <BookmarkSolidIcon aria-hidden="true" />
                                    ) : (
                                        <BookmarkIcon aria-hidden="true" />
                                    )}
                                    <span>{showBookmarkedOnly ? "Bookmarked" : "Bookmarks"}</span>
                                    {bookmarkedIds.size > 0 && <small>{bookmarkedIds.size}</small>}
                                </button>
                            )}
                        </div>
                    )}
                </section>

                <section className={styles.questionsStream}>
                    {(loading || authLoading) ? (
                        <div className={styles.loadingContainer}>
                            <div className={styles.loadingSpinner} />
                            <p>Loading...</p>
                        </div>
                    ) : specialties.length === 0 ? (
                        <div className={styles.emptyState}>
                            <p>No questions in this collection yet.</p>
                        </div>
                    ) : questionsLoading ? (
                        <div className={styles.loadingContainer}>
                            <div className={styles.loadingSpinner} />
                            <p>Loading {selectedSpecialtyName} questions...</p>
                        </div>
                    ) : questions.length === 0 ? (
                        <div className={styles.emptyState}>
                            <p>No questions for {selectedSpecialtyName} in this collection.</p>
                        </div>
                    ) : visibleQuestions.length === 0 ? (
                        <div className={styles.emptyState}>
                            <p>No bookmarked questions in {selectedSpecialtyName} yet.</p>
                        </div>
                    ) : (
                        <div className={styles.questionsList}>
                            {visibleQuestions
                                .map((question, index) => {
                                    const isRevealed = question.revealed;
                                    const isRevealing = revealingId === question.id;
                                    const isExpanded = expandedId === question.id;
                                    const isBookmarked = bookmarkedIds.has(question.id);
                                    const referencesOpen = !collapsedReferenceIds.has(question.id);
                                    const correctOption = question.options.find(option => option.label === question.correct_answer);

                                    return (
                                        <article
                                            key={question.id}
                                            id={`question-${question.id}`}
                                            className={`${styles.questionItem} ${isRevealed ? styles.revealed : ""} ${isExpanded ? styles.expanded : ""}`}
                                            onClick={(e) => toggleExpand(question.id, e.currentTarget)}
                                        >
                                            <div className={styles.questionSummary}>
                                                <div className={styles.questionNumber}>{index + 1}</div>

                                                <div className={styles.questionContent}>
                                                    <div className={styles.questionMeta}>
                                                        <span>{question.specialty}</span>
                                                        {question.difficulty && (
                                                            <span className={`${styles.difficultyTag} ${getDifficultyClass(question.difficulty)}`}>
                                                                {question.difficulty}
                                                            </span>
                                                        )}
                                                        {isRevealed && (
                                                            <span className={styles.revealedTag}>
                                                                <CheckIcon aria-hidden="true" />
                                                                Revealed
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className={styles.questionText}>
                                                        {question.question_text}
                                                    </p>
                                                </div>

                                                <div className={styles.questionActions}>
                                                    {user && (
                                                        <button
                                                            className={`${styles.bookmarkButton} ${isBookmarked ? styles.active : ""}`}
                                                            onClick={(e) => handleBookmark(e, question.id)}
                                                            title={isBookmarked ? "Remove bookmark" : "Bookmark this question"}
                                                            type="button"
                                                        >
                                                            {isBookmarked ? (
                                                                <BookmarkSolidIcon aria-hidden="true" />
                                                            ) : (
                                                                <BookmarkIcon aria-hidden="true" />
                                                            )}
                                                        </button>
                                                    )}
                                                    <button
                                                        className={styles.expandButton}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleExpand(
                                                                question.id,
                                                                (e.currentTarget.closest("article") as HTMLElement | null) || undefined
                                                            );
                                                        }}
                                                        type="button"
                                                        aria-expanded={isExpanded}
                                                        aria-controls={`question-body-${question.id}`}
                                                        aria-label={isExpanded ? "Collapse question" : "Expand question"}
                                                    >
                                                        <ChevronRightIcon
                                                            className={styles.expandIcon}
                                                            aria-hidden="true"
                                                        />
                                                    </button>
                                                </div>
                                            </div>

                                            {mountedQuestionIds.has(question.id) && (
                                                <div
                                                    id={`question-body-${question.id}`}
                                                    className={`${styles.questionBodyRegion} ${isExpanded ? styles.questionBodyRegionOpen : ""}`}
                                                    aria-hidden={!isExpanded}
                                                >
                                                    <div className={styles.questionBodyClip}>
                                                        <div className={styles.cardBody} onClick={(e) => e.stopPropagation()}>
                                                    <QBankQuestionImage image={question.question_image} />
                                                    <div className={styles.optionsPanel}>
                                                        <div className={styles.sectionLabel}>Options</div>
                                                        <div className={styles.optionsList}>
                                                            {question.options.map((option) => {
                                                                const isCorrect = isRevealed && option.label === question.correct_answer;
                                                                return (
                                                                    <div
                                                                        key={option.label}
                                                                        className={`${styles.optionItem} ${isCorrect ? styles.correctOption : ""}`}
                                                                    >
                                                                        <span className={styles.optionLabel}>
                                                                            {option.label}
                                                                        </span>
                                                                        <span className={styles.optionText}>
                                                                            {option.text}
                                                                        </span>
                                                                        {isCorrect && (
                                                                            <span className={styles.correctBadge}>
                                                                                <CheckIcon aria-hidden="true" />
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>

                                                    {isRevealed ? (
                                                        <div className={styles.answerSurface}>
                                                            <div className={styles.answerBlock}>
                                                                <h4>Answer</h4>
                                                                <p>
                                                                    Option {question.correct_answer}
                                                                    {correctOption ? `: ${correctOption.text}` : ""}
                                                                </p>
                                                            </div>

                                                            <div className={styles.surfaceDivider} />

                                                            <div className={styles.answerBlock}>
                                                                <h4>Explanation</h4>
                                                            {question.explainer_status === "processing" ? (
                                                                <div className={styles.processingNote}>
                                                                    <span className={styles.inlineSpinner} aria-hidden="true" />
                                                                    <p>Generating detailed explanation...</p>
                                                                </div>
                                                            ) : question.explanation ? (
                                                                <div className={styles.explanationContent}>
                                                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                                        {question.explanation}
                                                                    </ReactMarkdown>
                                                                </div>
                                                            ) : (
                                                                <p className={styles.explanationPending}>
                                                                    Explanation being prepared...
                                                                </p>
                                                            )}
                                                            </div>

                                                            {question.question_references && question.question_references.length > 0 && (
                                                                <div className={`${styles.referencesSection} ${referencesOpen ? styles.referencesSectionOpen : ""}`}>
                                                                    <button
                                                                        className={styles.referencesSummary}
                                                                        type="button"
                                                                        onClick={() => toggleReferences(question.id)}
                                                                        aria-expanded={referencesOpen}
                                                                        aria-controls={`question-references-${question.id}`}
                                                                    >
                                                                        <span>
                                                                            {question.question_references.length} {question.question_references.length === 1 ? "Reference" : "References"}
                                                                        </span>
                                                                        <ChevronRightIcon aria-hidden="true" />
                                                                    </button>
                                                                    <div
                                                                        id={`question-references-${question.id}`}
                                                                        className={`${styles.referencesContentRegion} ${referencesOpen ? styles.referencesContentRegionOpen : ""}`}
                                                                        aria-hidden={!referencesOpen}
                                                                    >
                                                                        <div className={styles.referencesContentClip}>
                                                                            {question.question_references.map((ref: any, i: number) => {
                                                                                const page = ref.page_number || ref.details?.page_number;
                                                                                let source = ref.source || ref.textbook || ref.source_name || "Source";
                                                                                if (source && typeof source === 'string') {
                                                                                    source = source.replace(/\.pdf$/i, "");
                                                                                }

                                                                                return (
                                                                                    <div key={i} className={styles.referenceItem}>
                                                                                        <div className={styles.referenceCitedText}>
                                                                                            <div className={styles.referenceCitedTextLabel}>Cited text</div>
                                                                                            <div>"{ref.excerpt || ref.content || ref.text_excerpt}"</div>
                                                                                        </div>

                                                                                        <div className={styles.referenceMeta}>
                                                                                            <div className={styles.referenceSource}>
                                                                                                <div className={styles.referenceSourceLabel}>Source</div>
                                                                                                <div className={styles.referenceSourceText}>{source}</div>
                                                                                            </div>

                                                                                            {page && (
                                                                                                <div className={styles.referencePageNumber}>
                                                                                                    <div className={styles.referencePageLabel}>Page</div>
                                                                                                    <div className={styles.referencePageText}>{page}</div>
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}

                                                            <div className={styles.actionButtons}>
                                                                <button
                                                                    className={`${styles.actionButton} ${styles.secondaryAction}`}
                                                                    onClick={(e) => handleShare(e, question)}
                                                                    type="button"
                                                                >
                                                                    {copiedId === question.id ? (
                                                                        <>
                                                                            <ClipboardDocumentCheckIcon aria-hidden="true" />
                                                                            Copied!
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <ClipboardDocumentIcon aria-hidden="true" />
                                                                            Share
                                                                        </>
                                                                    )}
                                                                </button>
                                                                <button
                                                                    className={`${styles.actionButton} ${styles.primaryAction}`}
                                                                    onClick={(e) => handleDiscussWithAI(e, question)}
                                                                    type="button"
                                                                    disabled={Boolean(question.question_image)}
                                                                    title={question.question_image ? "AI discussion is unavailable because Agent6 cannot inspect the attached image." : undefined}
                                                                >
                                                                    <ChatBubbleLeftRightIcon aria-hidden="true" />
                                                                    {question.question_image ? "AI discussion unavailable" : "Discuss with AI"}
                                                                </button>
                                                                <button
                                                                    className={`${styles.actionButton} ${styles.reportAction}`}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setReportingQuestionId(question.id);
                                                                    }}
                                                                    title="Report an issue"
                                                                    type="button"
                                                                >
                                                                    <FlagIcon aria-hidden="true" />
                                                                    Report
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className={styles.revealSection}>
                                                            <button
                                                                className={styles.revealButton}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (!user && isSalesEntry) {
                                                                        handleCampaignUpgrade(question.id);
                                                                    } else {
                                                                        handleReveal(e, question.id);
                                                                    }
                                                                }}
                                                                disabled={isRevealing}
                                                                type="button"
                                                            >
                                                                {!user && isSalesEntry
                                                                    ? isArabicCampaign
                                                                        ? "افتح الإجابات والاختبارات التجريبية"
                                                                        : "Unlock answers + mock exams"
                                                                    : !user
                                                                    ? "Sign in to Reveal Answer"
                                                                    : isRevealing
                                                                        ? "Revealing..."
                                                                        : "Reveal Answer"}
                                                            </button>
                                                            <p className={styles.revealHint}>
                                                                {!user && isSalesEntry
                                                                    ? isArabicCampaign
                                                                        ? "تمنحك Premium Plus منظومة التحضير الكاملة مع المراجع"
                                                                        : "Premium Plus gives you the complete referenced study system"
                                                                    : !user
                                                                    ? "Question and options are free to browse"
                                                                    : subscriptionPlan
                                                                        ? "Included with your subscription"
                                                                        : `Uses 1 credit • You have ${credits}`}
                                                            </p>
                                                            {!user && isSalesEntry && (
                                                                <button
                                                                    type="button"
                                                                    className={styles.campaignSignInLink}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleGuestSignIn(question.id);
                                                                    }}
                                                                >
                                                                    {isArabicCampaign ? "لديك اشتراك؟ سجل الدخول" : "Already have access? Sign in"}
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}

                                                            {user && renderSimilarQuestions(question.id)}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </article>
                                    );
                                })}
                        </div>
                    )}
                </section>
            </main>

            <Footer language={campaignLanguage} />

            {/* Report Modal */}
            {reportingQuestionId && user?.id && (
                <ReportModal
                    questionId={reportingQuestionId}
                    userId={user.id}
                    onClose={() => setReportingQuestionId(null)}
                    onSubmit={handleReport}
                />
            )}
        </div>
    );
};

export default QBankCollection;
